import { Form } from "react-router";
import { BERUFSSTATUS_FALLBACK } from "../hooks/useBerufsstatusOptions";

type Path = string; // e.g. "kontaktTyp"

type Condition =
  | { eq: [Path, string | number | boolean] }
  | { all: Condition[] }
  | { any: Condition[] }
  | { not: Condition }
  | { field: string; operator: "eq" | "regex"; value: any };

export type Field = {
  name: string;
  label: string;
  fieldType: string;
  defaultValue?: string;
  placeholder?: string;
  content?: string[];
  info?: string;
  visibleWhen?: Condition; // NEW: per-field visibility
};

export type Step = {
  name: string;
  repeat?: boolean;
  fields: Field[];
  visibleWhen?: Condition; // NEW: per-field visibility
};

export type Form = {
  name: string;
  header: string;
  steps: Step[];
  visibleWhen?: Condition; // NEW: per-field visibility
};

function get(obj: any, path: Path) {
  // If flat object, no split needed usually, but logic kept for safety
  if (!path) return undefined;
  return path.split(".").reduce((o, k) => (o ? o[k] : undefined), obj);
}

export function evalCond(cond: Condition | undefined, data: any): boolean {
  if (!cond) return true;
  if ("field" in cond) {
    const val = get(data, cond.field);
    if (cond.operator === "eq") return val === cond.value;
    if (cond.operator === "regex") return new RegExp(cond.value).test(String(val || ""));
    return false;
  }
  if ("eq" in cond) {
    const [p, v] = cond.eq;
    return get(data, p) === v;
  }
  if ("all" in cond) return cond.all.every((c) => evalCond(c, data));
  if ("any" in cond) return cond.any.some((c) => evalCond(c, data));
  if ("not" in cond) return !evalCond(cond.not, data);
  return true;
}

const childrenForm = (header: string = "Kind erfassen") => {
  return {
    name: "children",
    header: header,
    steps: [
      {
        name: "Basisdaten",
        fields: [
          {
            name: "firstname",
            label: "Vorname (gemäß Personalausweis)",
            fieldType: "TextInput",
            placeholder: "Max",
          },
          {
            name: "lastname",
            label: "Nachname",
            fieldType: "TextInput",
            placeholder: "Mustermann",
          },
          {
            name: "birthPlace",
            label: "Geburtsort",
            fieldType: "TextInput",
            placeholder: "Aachen",
          },
          {
            name: "birthDate",
            label: "Geburtsdatum",
            fieldType: "DateInput",
          },
        ],
      },
    ],
  } as Form;
};
const create_children_form = (header: string) => { };

const companyForm: Form = {
  name: "kunde_firma",
  header: "Firmenkontakt anlegen",
  steps: [
    // 1) Allgemein
    {
      name: "Allgemein",
      fields: [
        {
          name: "foundedAt",
          label: "Gründungsdatum",
          fieldType: "TextInput",
          placeholder: "TT.MM.JJJJ",
        },
        {
          name: "predecessorName",
          label: "Rechtsvorgänger (falls vorhanden)",
          fieldType: "TextInput",
          placeholder: "z. B. Alt-Firma GmbH",
        },
        {
          name: "balanceDate",
          label: "Geschäftsjahr (Bilanzstichtag)",
          fieldType: "TextInput",
          placeholder: "TT.MM",
        },
        {
          name: "vatDeductible",
          label: "Vorsteuerabzugsberechtigung",
          fieldType: "Dropdown",
          placeholder: "Bitte auswählen",
          content: ["Nein", "Ja"],
        },
      ],
    },

    // 3) kommunikation
    {
      name: "Kommunikation",
      fields: [
        {
          name: "phone",
          label: "Telefon-Nr.",
          fieldType: "TextInput",
          placeholder: "+49 221 123456",
        },
        {
          name: "fax",
          label: "Fax-Nr.",
          fieldType: "TextInput",
          placeholder: "+49 221 654321",
        },
        {
          name: "email",
          label: "E-Mail",
          fieldType: "TextInput",
          placeholder: "info@firma.de",
        },
        {
          name: "website",
          label: "Internet",
          fieldType: "TextInput",
          placeholder: "https://www.firma.de",
        },
      ],
    },
    {
      name: "Social Media",
      fields: [
        {
          name: "linkedin",
          label: "LinkedIn",
          fieldType: "TextInput",
          placeholder: "https://www.linkedin.com/in/firma",
        },
        {
          name: "instagram",
          label: "Instagram",
          fieldType: "TextInput",
          placeholder: "https://www.instagram.com/firma",
        },
        {
          name: "facebook",
          label: "Facebook",
          fieldType: "TextInput",
          placeholder: "https://www.facebook.com/firma",
        },
        {
          name: "tiktok",
          label: "TikTok",
          fieldType: "TextInput",
          placeholder: "https://www.tiktok.com/@firma",
        },
        {
          name: "xing",
          label: "XING",
          fieldType: "TextInput",
          placeholder: "https://www.xing.com/pages/firma",
        },
        {
          name: "youtube",
          label: "YouTube",
          fieldType: "TextInput",
          placeholder: "https://www.youtube.com/@firma",
        },
      ],
    },
    // 4) weitere kommunikation
    {
      name: "Weitere Kommunikation",
      fields: [
        {
          name: "newsletter",
          label: "Newsletter vorhanden?",
          fieldType: "Radio",
          content: ["Nein", "Ja"],
        },
        {
          name: "newsletterText",
          label: "",
          fieldType: "TextInput",
          placeholder: "Newsletter eingeben",
          visibleWhen: { eq: ["newsletter", "Ja"] },
        },
        {
          name: "whatsapp",
          label: "Whatsapp-Kanal vorhanden?",
          fieldType: "Radio",
          content: ["Nein", "Ja"],
        },
        {
          name: "whatsappText",
          label: "",
          fieldType: "TextInput",
          placeholder: "Whatsapp-Kanal eingeben",
          visibleWhen: { eq: ["whatsapp", "Ja"] },
        },
        {
          name: "otherChannels",
          label: "Sonstiges vorhanden ?",
          fieldType: "Radio",
          content: ["Nein", "Ja"],
        },
        {
          name: "otherChannelsText",
          label: "",
          fieldType: "TextInput",
          placeholder: "Sonstiges eingeben",
          visibleWhen: { eq: ["otherChannels", "Ja"] },
        },
      ],
    },    
    // 4) Bank
    {
      name: "Bank",
      fields: [
        {
          name: "accountHolder",
          label: "Kontoinhaber",
          fieldType: "TextInput",
          placeholder: "z. B. Max Mustermann",
          defaultValue: "",
        },
        {
          name: "iban",
          label: "IBAN",
          fieldType: "TextInput",
          placeholder: "DE00 0000 0000 0000 0000 00",
        },
        {
          name: "bic",
          label: "BIC",
          fieldType: "TextInput",
          placeholder: "BANKDEFFXXX",
        },
        {
          name: "bankname",
          label: "Kreditinstitut",
          fieldType: "TextInput",
          placeholder: "z. B. Deutsche Bank",
        },
        {
          name: "bankbezeichnung",
          label: "Bezeichnung",
          fieldType: "TextInput",
          placeholder: "z. B. Hauptkonto",
        },
        {
          name: "additionalAccounts",
          label: "Weitere Banken erfassen?",
          fieldType: "additionalAccounts",
        }
      ],
    },

    // 5) Sonstiges
    {
      name: "Sonstiges",
      fields: [
        {
          name: "memberships",
          label: "Mitgliedschaften in Verbänden / Vereinen / Interessenvertretungen",
          fieldType: "TextInput",
          placeholder: "z. B. VDMA, BDI …",
        },
        {
          name: "photo_url",
          label: "Logo Ihres Unternehmens",
          fieldType: "FileUpload",
          placeholder: "Unternehemenslogo hochladen",
        },
      ],
    },

    // 6) Rollen, Ansprechpartner, Funktionen im Unternehmen und fürs Unternehmen
    {
      name: "Rollen, Ansprechpartner, Funktionen im Unternehmen und fürs Unternehmen",
      fields: [
        {
          name: "rolesInCompany",
          label: "Rollen im Unternehmen (z.B. Inhaber, Geschäftsführer, Prokurist, Vorstand, Aufsichtsrat etc.)",
          fieldType: "RelatedEmployeesRoles",
          placeholder: "Details zu Rollen im Unternehmen...",
        },
      ],
    },

    // 7) Verknüpfungen
    {
      name: "Verknüpfungen",
      fields: [
        {
          name: "relationToPrivateContacts",
          label: "Beziehung zu Privat-Kontakten?",
          fieldType: "RelatedContacts",
          placeholder: "",
        },
        {
          name: "relationToCompanyContacts",
          label: "Beziehung zu Unternehmenskontakten?",
          fieldType: "RelatedContacts",
          placeholder: "",
        },
        {
          name: "belongsToCustomerGroup",
          label: "Gehört der Kontakt zu einer Kundenverbindung?",
          fieldType: "CustomerGroup",
          placeholder: "",
        },
      ],
    },

    // 8) Analyse
    {
      name: "Analyse",
      fields: [
        {
          name: "collectiveAgreement",
          label: "Unterliegen Sie einem Tarifvertrag?",
          fieldType: "Radio",
          content: ["Nein", "Ja"],
        },
        {
          name: "hasIndependentBranches",
          label: "Gibt es rechtlich unselbstständige Niederlassungen im In- oder Ausland?",
          fieldType: "TextArea",
          placeholder: "Details zu Niederlassungen...",
        },
        {
          name: "operatingSites",
          label: "Betriebsstätten (Risikoort - wo, wieviele, wie genutzt)? + Lagerorte + Standorte im Ausland",
          fieldType: "TextArea",
          placeholder: "Details zu Standorten...",
        },
        {
          name: "businessCharacter",
          label: "Betriebscharakter, welche Tätigkeiten werden ausgeführt? (Werden betriebsunübliche Tätigkeiten ausgeübt? Sind Sie im Ausland tätig?)",
          fieldType: "TextArea",
          placeholder: "Beschreibung der Tätigkeiten...",
        },
        {
          name: "companyHeadquartersInfo",
          label: "Firmensitz (Gibt es weitere selbständige Firmen z.B. Tochterunternehmen etc.)",
          fieldType: "TextArea",
          placeholder: "Details zum Firmensitz und Töchtern...",
        },
        {
          name: "customersInfo",
          label: "Kunden (größte und Anteil Privat, Öffentliche, Gewerbe, Sonstige)",
          fieldType: "TextArea",
          placeholder: "Kundenstruktur...",
        },
        {
          name: "competitors",
          label: "Wettbewerber (welche, Marktanteile, etc.)",
          fieldType: "TextArea",
          placeholder: "Details zu Wettbewerbern...",
        },
        {
          name: "propertiesInfo",
          label: "Besitzen Sie eigene Grundstücke und Gebäude oder sind solche in Planung/im Bau?",
          fieldType: "Radio",
          defaultValue: "Nein",
          content: ["Nein", "Ja"],
        },
        {
          name: "assetsInfo",
          label: "Besitzen Sie Betriebsvermögen (außer Immobilien und KFZ) wie kfm. u. tech. Betriebseinrichtung, Waren, etc.",
          fieldType: "Radio",
          defaultValue: "Nein",
          content: ["Nein", "Ja"],
        },
        {
          name: "vehiclesInfo",
          label: "Besitzen Sie KFZ oder geleaste oder gemietete?",
          fieldType: "Radio",
          defaultValue: "Nein",
          content: ["Nein", "Ja"],
        },
        {
          name: "hasTransportRisks",
          label: "Möchten Sie Transport Risiken erfassen?",
          fieldType: "Radio",
          defaultValue: "Nein",
          content: ["Nein", "Ja"],
        },
        {
          name: "transportRisks",
          label: "Transport Risiken",
          fieldType: "TransportRisk",
          placeholder: "",
        },
        {
          name: "hasCyberRisks",
          label: "Möchten Sie Cyber Risiken erfassen?",
          fieldType: "Radio",
          defaultValue: "Nein",
          content: ["Nein", "Ja"],
        },
        {
          name: "cyberRisks",
          label: "Cyber Risiken",
          fieldType: "CyberRisk",
          placeholder: "",
        },
        {
          name: "hasBusinessInterruptionRisks",
          label: "Möchten Sie Ertragsausfall Risiken erfassen?",
          fieldType: "Radio",
          defaultValue: "Nein",
          content: ["Nein", "Ja"],
        },
        {
          name: "businessInterruptionRisks",
          label: "Ertragsausfall Risiken",
          fieldType: "BusinessInterruptionRisk",
          placeholder: "",
        },
        {
          name: "hasFinanceRisks",
          label: "Möchten Sie Finanz Risiken erfassen?",
          fieldType: "Radio",
          defaultValue: "Nein",
          content: ["Nein", "Ja"],
        },
        {
          name: "financeRisks",
          label: "Finanz Risiken",
          fieldType: "FinanceRisk",
          placeholder: "",
        },
        {
          name: "hasHumanResourceRisks",
          label: "Möchten Sie Human-Ressource Risiken erfassen?",
          fieldType: "Radio",
          defaultValue: "Nein",
          content: ["Nein", "Ja"],
        },
        {
          name: "humanResourceRisks",
          label: "Human-Ressource Risiken",
          fieldType: "HumanResourceRisk",
          placeholder: "",
        },
      ],
    },
  ],
};


const partner: Form = {
  name: "kunde_privat",
  header: "Partner erfassen",
  steps: [
    {
      name: "Name",
      fields: [
        {
          name: "anrede",
          label: "Anrede",
          fieldType: "Dropdown",
          defaultValue: "",
          placeholder: "Anrede auswählen",
          content: ["Herr", "Frau", "Divers"],
        },
        {
          name: "title",
          label: "Titel",
          fieldType: "Dropdown",
          defaultValue: "",
          placeholder: "Titel auswählen",
          content: [
            "Dipl. Ing.",
            "Dipl.-Kfm.",
            "Dr.",
            "Dr. jur.",
            "Dr. med.",
            "Drs.",
            "Prof. Dr.",
            "Steuerberater",
          ],
        },
        {
          name: "lastname",
          label: "Nachname",
          fieldType: "TextInput",
          placeholder: "Mustermann",
        },
        {
          name: "firstname",
          label: "Vorname (gemäß Personalausweis)",
          fieldType: "TextInput",
          placeholder: "Max",
        },
        {
          name: "placeofbirth",
          label: "Geburtsort",
          fieldType: "TextInput",
          placeholder: "z. B. München",
        },
        {
          name: "dateofbirth",
          label: "Geburtsdatum",
          fieldType: "DateInput",
          placeholder: "TT.MM.JJJJ",
        },
        {
          name: "birthname",
          label: "Geburtsname",
          fieldType: "TextInput",
          placeholder: "Nur falls abweichend",
        },
        {
          name: "citizenship",
          label: "Staatsangehörigkeit",
          fieldType: "TextInput",
          placeholder: "z. B. Deutsch",
        },
      ],
    },
    {
      name: "Anschrift",
      fields: [
        {
          name: "street",
          label: "Straße und Hausnummer",
          fieldType: "TextInput",
          placeholder: "z. B. Hauptstraße 15",
        },
        {
          name: "postalcode",
          label: "Postleitzahl",
          fieldType: "TextInput",
          placeholder: "z. B. 80331",
        },
        {
          name: "city",
          label: "Ort",
          fieldType: "TextInput",
          placeholder: "z. B. München",
        },
        {
          name: "city_area",
          label: "Ortsteil",
          fieldType: "TextInput",
          placeholder: "Sendling",
        },
      ],
    },
    {
      name: "Kommunikation",
      fields: [
        {
          name: "phone",
          label: "Telefon",
          fieldType: "TextInput",
          placeholder: "+49 89 123456",
        },
        {
          name: "mobile",
          label: "Mobil",
          fieldType: "TextInput",
          placeholder: "+49 171 1234567",
        },
        {
          name: "fax",
          label: "Fax",
          fieldType: "TextInput",
          placeholder: "+49 89 987654",
        },
        {
          name: "email",
          label: "E-Mail",
          fieldType: "TextInput",
          placeholder: "beispiel@domain.de",
        },
      ],
    },
    {
      name: "Beruf",
      fields: [
        {
          name: "occupationStatus",
          label: "Status",
          fieldType: "Dropdown",
          defaultValue: "",
          placeholder: "Berufsstatus auswählen",
          // EINE Quelle: der gepflegte Katalog `ContactBerufsstatus`. Hier stand
          // bis 08/2026 eine eigene 5er-Liste („Angestellt", „Selbstständig",
          // „Beamte", „Azubi", „Sonstige"), die bis auf „Sonstige" disjunkt zum
          // Katalog war — ein gespeicherter Katalogwert erschien im Dropdown als
          // leer. Diese Datei ist statisch und kann den Endpoint nicht abfragen,
          // deshalb der Konstanten-Spiegel; die Masken laden über
          // `useBerufsstatusOptions` die echten Katalogwerte.
          content: [...BERUFSSTATUS_FALLBACK],
        },
        {
          name: "employer",
          label: "Arbeitgeber",
          fieldType: "TextInput",
          placeholder: "z. B. Musterfirma GmbH",
        },
        {
          name: "industry",
          label: "Branche",
          fieldType: "TextInput",
          placeholder: "z. B. Versicherungen",
        },
        {
          name: "position",
          label: "Tätigkeit",
          fieldType: "TextInput",
          placeholder: "z. B. Vertriebsleiter",
        },
      ],
    },
    {
      name: "Bank",
      fields: [
        {
          name: "accountHolder",
          label: "Kontoinhaber",
          fieldType: "TextInput",
          placeholder: "z. B. Max Mustermann",
          defaultValue: "",
        },
        {
          name: "iban",
          label: "IBAN",
          fieldType: "TextInput",
          placeholder: "DE00 0000 0000 0000 0000 00",
        },
        {
          name: "bic",
          label: "BIC",
          fieldType: "TextInput",
          placeholder: "BANKDEFFXXX",
        },
        {
          name: "bankname",
          label: "Kreditinstitut",
          fieldType: "TextInput",
          placeholder: "z. B. Deutsche Bank",
        },
        {
          name: "bankbezeichnung",
          label: "Bezeichnung",
          fieldType: "TextInput",
          placeholder: "z. B. Hauptkonto",
        },
      ],
    },
    {
      name: "Sonstiges",
      fields: [
        {
          name: "customerManagerBackoffice",
          label: "Kontaktverantwortlicher (Backoffice)",
          fieldType: "Dropdown",
          placeholder: "Mitarbeiter auswählen",
        },
        {
          name: "customerManagerFrontoffice",
          label: "Kontaktverantwortlicher (Frontoffice)",
          fieldType: "Dropdown",
          placeholder: "Mitarbeiter auswählen",
        },
        {
          name: "referrer",
          label: "Empfehlungsgeber",
          fieldType: "TextInput",
          placeholder: "Name oder Firma des Empfehlungsgebers",
        },
        {
          name: "familyStatus",
          label: "Was ist Ihr Familienstand?",
          fieldType: "Radio",
          defaultValue: "",
          placeholder: "",
          content: ["Ledig", "Verheiratet", "Geschieden", "Verwitwet"],
        },
      ],
    },
  ],
};
const privat_contact_form: Form = {
  name: "kunde_privat",
  // Gegenstück zu „Firmenkontakt anlegen" — der Typ „Person" heißt in der
  // Oberfläche „Privat" (utils/contactTypeLabel.ts).
  header: "Privatkontakt anlegen",
  steps: [
    {
      name: "Allgemein",
      fields: [
        {
          name: "placeofbirth",
          label: "Geburtsort",
          fieldType: "TextInput",
          placeholder: "z. B. München",
        },
        {
          name: "dateofbirth",
          label: "Geburtsdatum",
          fieldType: "DateInput",
          placeholder: "TT.MM.JJJJ",
        },
        {
          name: "birthname",
          label: "Geburtsname",
          fieldType: "TextInput",
          placeholder: "Nur falls abweichend",
        },
        {
          name: "citizenship",
          label: "Staatsangehörigkeit",
          fieldType: "TextInput",
          placeholder: "z. B. Deutsch",
        },
      ],
    },
    {
      name: "Kommunikation",
      fields: [
        {
          name: "phonePrivate",
          label: "Telefon (privat)",
          fieldType: "TextInput",
          placeholder: "+49 89 123456",
        },
        {
          name: "phoneWork",
          label: "Telefon (geschäftlich)",
          fieldType: "TextInput",
          placeholder: "+49 89 123456",
        },
        {
          name: "mobilePrivate",
          label: "Mobil (privat)",
          fieldType: "TextInput",
          placeholder: "+49 171 1234567",
        },
        {
          name: "mobileWork",
          label: "Mobil (geschäftlich)",
          fieldType: "TextInput",
          placeholder: "+49 171 1234567",
        },
        {
          name: "emailPrivate",
          label: "E-Mail (privat)",
          fieldType: "TextInput",
          placeholder: "beispiel@domain.de",
        },
        {
          name: "emailWork",
          label: "E-Mail (geschäftlich)",
          fieldType: "TextInput",
          placeholder: "beispiel@domain.de",
        },
      ],
    },
    {
      name: "Social Media ",
      fields: [
        {
          name: "linkedin",
          label: "LinkedIn",
          fieldType: "TextInput",
          placeholder: "https://www.linkedin.com/in/firma",
        },
        {
          name: "instagram",
          label: "Instagram",
          fieldType: "TextInput",
          placeholder: "https://www.instagram.com/firma",
        },
        {
          name: "facebook",
          label: "Facebook",
          fieldType: "TextInput",
          placeholder: "https://www.facebook.com/firma",
        },
        {
          name: "tiktok",
          label: "TikTok",
          fieldType: "TextInput",
          placeholder: "https://www.tiktok.com/@firma",
        },
        {
          name: "xing",
          label: "XING",
          fieldType: "TextInput",
          placeholder: "https://www.xing.com/profile/...",
        },
        {
          name: "youtube",
          label: "YouTube",
          fieldType: "TextInput",
          placeholder: "https://www.youtube.com/@...",
        },
      ],
    },
    {
      name: "Beruf",
      fields: [
        {
          name: "occupationStatus",
          label: "Status",
          fieldType: "Dropdown",
          defaultValue: "",
          placeholder: "Berufsstatus auswählen",
          // EINE Quelle: der gepflegte Katalog `ContactBerufsstatus`. Hier stand
          // bis 08/2026 eine eigene 5er-Liste („Angestellt", „Selbstständig",
          // „Beamte", „Azubi", „Sonstige"), die bis auf „Sonstige" disjunkt zum
          // Katalog war — ein gespeicherter Katalogwert erschien im Dropdown als
          // leer. Diese Datei ist statisch und kann den Endpoint nicht abfragen,
          // deshalb der Konstanten-Spiegel; die Masken laden über
          // `useBerufsstatusOptions` die echten Katalogwerte.
          content: [...BERUFSSTATUS_FALLBACK],
        },
        {
          name: "employer",
          label: "Arbeitgeber",
          fieldType: "TextInput",
          placeholder: "z. B. Musterfirma GmbH",
        },
        {
          name: "industry",
          label: "Branche",
          fieldType: "TextInput",
          placeholder: "z. B. Versicherungen",
        },
        {
          name: "position",
          label: "Tätigkeit",
          fieldType: "TextInput",
          placeholder: "z. B. Vertriebsleiter",
        },
      ],
    },
    {
      name: "Bank",
      fields: [
        {
          name: "accountHolder",
          label: "Kontoinhaber",
          fieldType: "TextInput",
          placeholder: "z. B. Max Mustermann",
        },
        {
          name: "iban",
          label: "IBAN",
          fieldType: "TextInput",
          placeholder: "DE00 0000 0000 0000 0000 00",
        },
        {
          name: "bic",
          label: "BIC",
          fieldType: "TextInput",
          placeholder: "BANKDEFFXXX",
        },
        {
          name: "bankname",
          label: "Kreditinstitut",
          fieldType: "TextInput",
          placeholder: "z. B. Deutsche Bank",
        },
        {
          name: "bankbezeichnung",
          label: "Bezeichnung",
          fieldType: "TextInput",
          placeholder: "z. B. Hauptkonto",
        },
        {
          name: "additionalAccounts",
          label: "Weitere Banken erfassen?",
          fieldType: "additionalAccounts",
        }
      ],
    },
    {
      name: "Sonstiges",
      fields: [
        {
          name: "photo_url",
          label: "Foto",
          fieldType: "FileUpload",
          placeholder: "Bild hochladen",
        },
        {
          name: "familyStatus",
          label: "Was ist Ihr Familienstand?",
          fieldType: "Radio",
          defaultValue: "",
          placeholder: "",
          content: ["Ledig", "Verheiratet", "Geschieden", "Verwitwet", "Sonstiges"],
        },
        {
          name: "familyStatusChildren",
          label: "Haben Sie Kinder?",
          fieldType: "Radio",
          defaultValue: "",
          placeholder: "",
          content: ["Nein", "Ja"],
        },

      ],
    },
    {
      name: "Verknüpfungen",
      fields: [
        {
          name: "relationToPrivateContacts",
          label: "Beziehung zu Privat-Kontakten?",
          fieldType: "RelatedContacts",
          placeholder: "",
        },
        {
          name: "relationToCompanyContacts",
          label: "Beziehung zu Unternehmenskontakten?",
          fieldType: "RelatedContacts",
          placeholder: "",
        },
        {
          name: "householdMembers",
          label: "Personen im Haushalt?",
          fieldType: "HouseholdMembers",
          placeholder: "",
        },
      ],
    },
  ],
};

const contactPersonForm: Form = {
  name: "contact_person",
  header: "Ansprechpartner erfassen",
  steps: [
    {
      name: "Basisdaten",
      repeat: true,
      fields: [
        {
          name: "contactSalutation",
          label: "Anrede",
          fieldType: "Dropdown",
          placeholder: "Anrede auswählen",
          content: ["Herr", "Frau", "Divers"],
        },
        {
          name: "contactTitle",
          label: "Titel auswählen",
          fieldType: "TextInput",
          placeholder: "z. B. Dr.",
        },
        {
          name: "contactLastname",
          label: "Nachname",
          fieldType: "TextInput",
          placeholder: "Nachname",
        },
        {
          name: "contactFirstname",
          label: "Vorname (gemäß Personalausweis)",
          fieldType: "TextInput",
          placeholder: "Vorname",
        },
        {
          name: "contactRole",
          label: "Rolle",
          fieldType: "TextInput",
          placeholder: "z. B. Mitarbeiter, Buchhalter",
        },
        {
          name: "contactFunction",
          label: "Funktion",
          fieldType: "TextInput",
          placeholder: "z. B. Ansprechpartner",
        },
        {
          name: "contactBirthdate",
          label: "Geburtsdatum ",
          fieldType: "TextInput",
          placeholder: "TT.MM.JJJJ",
        },
        {
          name: "contactPhone",
          label: "Telefon geschäftlich",
          fieldType: "TextInput",
          placeholder: "+49 123 456789",
        },
        {
          name: "contactPhone2",
          label: "Telefon privat",
          fieldType: "TextInput",
          placeholder: "+49 123 456789",
        },
        {
          name: "contactMobile",
          label: "Mobil",
          fieldType: "TextInput",
          placeholder: "+49 123 456789",
        },
        {
          name: "contactMobile2",
          label: "Mobil privat",
          fieldType: "TextInput",
          placeholder: "+49 123 456789",
        },
        {
          name: "contactEmail",
          label: "E-Mail geschäftlich",
          fieldType: "TextInput",
          placeholder: "beispiel@domain.de",
        },
        {
          name: "contactEmail2",
          label: "E-Mail privat",
          fieldType: "TextInput",
          placeholder: "beispiel@domain.de",
        },
        {
          name: "contactFax",
          label: "Fax ",
          fieldType: "TextInput",
          placeholder: "+49 123 987654",
        },
        {
          name: "contactAvailability",
          label: "Erreichbarkeit",
          fieldType: "TextInput",
          placeholder: "z. B. werktags 8-16 Uhr",
        },
      ],
    },
  ],
};

const shareholderForm: Form = {
  name: "shareholder_form",
  header: "Anteilseigner / Gesellschafter erfassen",
  steps: [
    {
      name: "Basisdaten",
      fields: [
        {
          name: "shareholderSalutation",
          label: "Anrede",
          fieldType: "Dropdown",
          placeholder: "Anrede auswählen",
          content: ["Herr", "Frau", "Divers"],
        },
        {
          name: "shareholderTitle",
          label: "Titel ",
          fieldType: "TextInput",
          placeholder: "z. B. Dr.",
        },
        {
          name: "shareholderLastname",
          label: "Nachname",
          fieldType: "TextInput",
          placeholder: "Nachname",
        },
        {
          name: "shareholderFirstname",
          label: "Vorname (gemäß Personalausweis)",
          fieldType: "TextInput",
          placeholder: "Vorname",
        },
        {
          name: "shareholderRole",
          label: "Rolle",
          fieldType: "TextInput",
          placeholder: "z. B. Gesellschafter",
        },
        {
          name: "shareholderFunction",
          label: "Funktion",
          fieldType: "TextInput",
          placeholder: "Anpsprechpartner",
        },
        {
          name: "shareholderBirthdate",
          label: "Geburtsdatum ",
          fieldType: "TextInput",
          placeholder: "TT.MM.JJJJ",
        },
        {
          name: "shareholderPhone",
          label: "Telefon (geschäftlich)",
          fieldType: "TextInput",
          placeholder: "+49 123 456789",
        },
        {
          name: "shareholderPhonePrivate",
          label: "Telefon (privat)",
          fieldType: "TextInput",
          placeholder: "+49 123 456789",
        },
        {
          name: "shareholderMobile",
          label: "Mobil (geschäftlich)",
          fieldType: "TextInput",
          placeholder: "+49 123 456789",
        },
        {
          name: "shareholderMobilePrivate",
          label: "Mobil (privat)",
          fieldType: "TextInput",
          placeholder: "+49 123 456789",
        },
        {
          name: "shareholderEmail",
          label: "E-Mail",
          fieldType: "TextInput",
          placeholder: "beispiel@domain.de",
        },
        {
          name: "shareholderEmailPrivate",
          label: "E-Mail (privat)",
          fieldType: "TextInput",
          placeholder: "beispiel@domain.de",
        },
        {
          name: "shareholderFax",
          label: "Fax ",
          fieldType: "TextInput",
          placeholder: "+49 123 987654",
        },
        {
          name: "shareholderAvailability",
          label: "Erreichbarkeit",
          fieldType: "TextInput",
          placeholder: "z. B. werktags 9-17 Uhr",
        },
        {
          name: "shareholderShares",
          label: "Anteile in %",
          fieldType: "TextInput",
          placeholder: "z. B. 50",
        },
      ],
    },
  ],
};

const buildingExtensionForm: Form = {
  name: "building_extension",
  header: "Aus-, und Anbau weiterer Bereiche",
  steps: [
    {
      name: "Gebäudeteile",
      fields: [
        {
          name: "hasBasement",
          label: "Hat das Haus einen Keller?",
          fieldType: "Radio",
          content: ["Nein", "Ja"],
        },
        {
          name: "hasOutbuildings",
          label: "Sind Garagen, Carports oder Nebengebäude vorhanden?",
          fieldType: "Radio",
          content: ["Nein", "Ja"],
        },
        {
          name: "numGaragesOutside",
          label: "Anzahl der Garagen außerhalb des Gebäudes",
          fieldType: "TextInput",
          placeholder: "z. B. 2",
        },
        {
          name: "numCarports",
          label: "Anzahl der Carports",
          fieldType: "TextInput",
          placeholder: "z. B. 1",
        },
        {
          name: "outbuildingArea",
          label: "Fläche der freistehenden Nebengebäude in m²",
          fieldType: "TextInput",
          placeholder: "z. B. 30",
        },
        {
          name: "outbuildingType",
          label: "Art des Nebengebäudes",
          fieldType: "Dropdown",
          defaultValue: "Sonstige",
          placeholder: "Bitte auswählen",
          content: ["Garage", "Carport", "Scheune", "Sonstige"],
        },
        {
          name: "isCommercialUse",
          label: "Wird das Gebäude gewerblich genutzt?",
          fieldType: "Radio",
          content: ["Nein", "Ja"],
        },
      ],
    },
  ],
};

const kennzahlenForm: Form = {
  name: "kennzahlen_form",
  header: "Kennzahlen erfassen",
  steps: [
    {
      name: "Basis-Kennzahlen",
      fields: [
        { name: "revenueTotal", label: "Umsatz Gesamt", fieldType: "CurrencyInput", placeholder: "0,00" },
        { name: "revenueInside", label: "Umsatz Innen", fieldType: "CurrencyInput", placeholder: "0,00" },
        { name: "revenueOutside", label: "Umsatz Außen", fieldType: "CurrencyInput", placeholder: "0,00" },
        { name: "revenueRegionDE", label: "Umsatz BRD", fieldType: "CurrencyInput", placeholder: "0,00" },
        { name: "revenueRegionEU", label: "Umsatz Europa (ohne BRD)", fieldType: "CurrencyInput", placeholder: "0,00" },
        { name: "revenueRegionNA", label: "Umsatz USA/Kanada", fieldType: "CurrencyInput", placeholder: "0,00" },
        { name: "revenueRegionRest", label: "Umsatz Rest", fieldType: "CurrencyInput", placeholder: "0,00" },
        { name: "cogsEur", label: "Wareneinsatz", fieldType: "CurrencyInput", placeholder: "0,00" },
        { name: "cogsPct", label: "Wareneinsatz (%)", fieldType: "TextInput", placeholder: "0" },
        { name: "grossProfit", label: "Rohertrag", fieldType: "CurrencyInput", placeholder: "0,00" },
        { name: "fixedAssets", label: "Anlagevermögen", fieldType: "CurrencyInput", placeholder: "0,00" },
        { name: "otherInfo", label: "Sonstiges", fieldType: "TextInput", placeholder: "" },
      ],
    },
    {
      name: "Risiko- & Kostenfelder",
      fields: [
        { name: "mgmtCost", label: "Aufwendungen GF", fieldType: "CurrencyInput", placeholder: "0,00" },
        { name: "tempLaborCost", label: "Leiharbeiter (Kosten)", fieldType: "CurrencyInput", placeholder: "0,00" },
        { name: "subcontractorCost", label: "Subunternehmer (Kosten)", fieldType: "CurrencyInput", placeholder: "0,00" },
        { name: "subcontractorHeadcount", label: "Subunternehmer (Anzahl)", fieldType: "TextInput", placeholder: "0" },
        { name: "travelKm", label: "Dienstreisekilometer", fieldType: "TextInput", placeholder: "0" },
        { name: "travelDays", label: "Dienstreisetage", fieldType: "TextInput", placeholder: "0" },
      ],
    },
    {
      name: "Mitarbeiter",
      fields: [
        { name: "avgConcurrentPersons", label: "Ø gleichzeitig tätige Personen", fieldType: "TextInput", placeholder: "0" },
        {
          name: "employeesMatrix",
          label: "Mitarbeiter Matrix",
          fieldType: "EmployeeMatrix",
          placeholder: "",
        },
      ],
    },
  ],
};

const kfzForm: Form = {
  name: "kfz_form",
  header: "KFZ erfassen",
  steps: [
    {
      name: "1. Fahrzeugart",
      fields: [
        {
          name: "vehicleClass",
          label: "Fahrzeugart",
          fieldType: "Dropdown",
          placeholder: "Bitte auswählen",
          content: [
            "Klasse 1 PKW",
            "Klasse 2 Lieferwagen Güterbeförderung bis 3.500kg",
            "Klasse 3 LKW Güterbeförderung über 3.500kg",
            "Klasse 4 Zugmaschine (Sattelzug, Land-/Forstwirtschaft, Unimog, etc.)",
            "Klasse 5 Busse",
            "Klasse 6 Anhänger/Auflieger",
            "Klasse 7 Arbeitsmaschinen",
            "Klasse 8 Gabelstapler",
            "Klasse 9 Krad",
            "Klasse 10 Sonderfahrzeuge",
          ],
        },
      ],
    },
    {
      name: "2. Kennzeichen",
      visibleWhen: { field: "vehicleClass", operator: "regex", value: "^Klasse" },
      fields: [
        {
          name: "licensePlate",
          label: "Kennzeichen",
          fieldType: "TextInput",
          placeholder: "z.B. AC-XP 123",
        },
        {
          name: "classicType",
          label: "Handelt es sich um einen Klassiker?",
          fieldType: "RadioGroup",
          content: ["Youngtimer > 20", "Oldtimer > 30"],
          visibleWhen: { eq: ["vehicleClass", "Klasse 1 PKW"] },
        },
        {
          name: "hLicensePlate",
          label: "H-Kennzeichen",
          fieldType: "RadioGroup",
          content: ["Nein", "Ja"],
          visibleWhen: {
            all: [
              { eq: ["vehicleClass", "Klasse 1 PKW"] },
              { eq: ["classicType", "Oldtimer > 30"] },
            ],
          },
        },
        {
          name: "registrationType",
          label: "Zulassungsart",
          fieldType: "RadioGroup",
          placeholder: "Bitte auswählen",
          content: ["Dauerhaft", "Saison"],
        },
        {
          name: "seasonStartEnd",
          label: "Saison von/bis",
          fieldType: "TextInput",
          placeholder: "z.B. 04/10",
          visibleWhen: { field: "registrationType", operator: "eq", value: "Saison" },
        },
        {
          name: "licensePlateType",
          label: "Kennzeichenart",
          fieldType: "RadioGroup",
          placeholder: "Bitte auswählen",
          content: ["Schwarz", "Grün", "Wechselkennzeichen"],
        },
      ],
    },
    {
      name: "3. Marke / Modell",
      visibleWhen: { field: "vehicleClass", operator: "regex", value: "^Klasse" },
      fields: [
        {
          name: "manufacturer",
          label: "Herstellername",
          fieldType: "Combobox",
          placeholder: "Marke suchen...",
          content: [
            "9ff", "Abarth", "AC", "ACM", "Acura", "Aerfal", "Aiways", "Aixam", "Alba Mobility", "Alfa Romeo",
            "Alpina", "Alpine", "Amphicar", "Angelelli Automobili", "Ariel Motor", "Artega", "Aspark", "Aspid",
            "Aston Martin", "Audi", "Aurus", "Austin", "Austin-Healey", "Autobianchi", "BAIC", "BAW", "Bedford",
            "Bellier", "Bentley", "Bertone", "Bestune", "BMW", "Boldmen", "Bolloré", "Borgward", "Brilliance",
            "Bristol", "Brute", "Bugatti", "Buick", "BYD", "Cadillac", "Caravans-Wohnm", "Carver", "Casalini",
            "Caterham", "Cenntro", "Chamonix", "Changan", "Changhe", "Chatenet", "Chery", "Chevrolet", "Chrysler",
            "Cirelli", "Citroen", "CityEL", "Corvette", "CUPRA", "Dacia", "Daewoo", "DAF", "Daihatsu", "Daimler",
            "Dallara", "Dangel", "De la Chapelle", "De Tomaso", "Delorean", "Denza", "Devinci Cars", "DFSK", "Dodge",
            "Dongfeng", "Donkervoort", "DR Automobiles", "DS Automobiles", "Dutton", "e.GO", "Ebro", "Econelo",
            "Edran", "Elaris", "Embuggy", "EMC", "Estrima", "Evetta", "EVO", "Farizon", "Ferrari", "Fiat", "Fisker",
            "Ford", "Forthing", "Foton", "Gac Gonow", "Galloper", "Gappy", "GAZ", "Geely", "GEM", "GEMBALLA",
            "Genesis", "Giana", "Gillet", "Giotti Victoria", "GMC", "Goupil", "Great Wall", "Grecav", "GTA", "GWM",
            "Haima", "Hamann", "Haval", "Hiphi", "Holden", "Honda", "Hongqi", "HUMMER", "Hurtan", "Hyundai", "ICH-X",
            "Ineos", "Infiniti", "Innocenti", "Invicta", "Iso Rivolta", "Isuzu", "Iveco", "IZH", "JAC", "Jaecoo",
            "Jaguar", "Jeep", "Jensen", "Karma", "KGM", "Kia", "Koenigsegg", "KTM", "Lada", "Lamborghini", "Lancia",
            "Land Rover", "LDV", "Leapmotor", "Lee Group", "LEVC", "Lexus", "Li", "Lifan", "Ligier", "Lincoln",
            "Linzda", "Livan", "Lorinser", "Lotus", "Lucid", "Lynk & Co", "M-Ero", "Mahindra", "MAN", "Mansory",
            "Martin", "Martin Motors", "Maserati", "Matra", "Maxus", "Maybach", "Mazda", "McLaren", "Mega", "Melex",
            "Mercedes-Benz", "Mercury", "MG", "Micro", "Microcar", "Militem", "Minari", "Minauto", "MINI", "Mitsubishi",
            "Mitsuoka", "Mobilize", "Morgan", "Moskvich", "MP Lafer", "MPM Motors", "NIO", "Nissan", "NSU", "Oldsmobile",
            "Oldtimer", "Omoda", "Opel", "Ora", "Pagani", "Panther Westwinds", "Peugeot", "PGO", "Piaggio", "Plymouth",
            "Polestar", "Pontiac", "Porsche", "Proton", "Puch", "RAM", "Regis", "Reliant", "Renault", "Rivian",
            "Rolls-Royce", "Rover", "Ruf", "Saab", "Santana", "SEAT", "Segway", "Selvo", "Seres", "Sevic", "SGS",
            "Shelby", "Shuanghuan", "Silence", "Simplici", "Singer", "Skoda", "Skywell", "smart", "SpeedArt",
            "Sportequipe", "Spyker", "SsangYong", "Stormborn", "StreetScooter", "Studebaker", "Subaru", "Suzuki", "SWM",
            "Talbot", "Tasso", "Tata", "Tazzari EV", "TECHART", "Tesla", "Tiger", "Togg", "Town Life", "Toyota",
            "Trabant", "Trailer-Anhänger", "Triumph", "Trucks-Lkw", "TVR", "UAZ", "Vanden Plas", "Vanderhall", "VAZ",
            "VEM", "VinFast", "Volkswagen", "Volvo", "Voyah", "Wartburg", "Weltmeister", "Wenckstern", "Westfield",
            "Wey", "Wiesmann", "XBus", "XEV", "Xiaomi", "Xpeng", "Zastava", "ZAZ", "Zeekr", "Zhidou", "Zotye", "Sonstige"
          ],
        },
        { name: "hsn", label: "Herstellernummer (HSN)", fieldType: "TextInput", placeholder: "" },
        { name: "typeName", label: "Typname", fieldType: "TextInput", placeholder: "" },
        { name: "tsn", label: "Typschlüsselnummer (TSN)", fieldType: "TextInput", placeholder: "" },
        { name: "vin", label: "Fahrzeugidentnummer", fieldType: "TextInput", placeholder: "" },
        {
          name: "driveType",
          label: "Antrieb",
          fieldType: "Dropdown",
          placeholder: "Bitte auswählen",
          content: ["Verbrenner", "Hybrid", "Elektro", "Gas", "Wasserstoff"],
        },
        {
          name: "eLicensePlate",
          label: "E-Kennzeichen",
          fieldType: "RadioGroup",
          content: ["Nein", "Ja"],
          visibleWhen: { field: "driveType", operator: "regex", value: "Elektro|Hybrid" },
        },
        { name: "powerKw", label: "Motorleistung in kW", fieldType: "TextInput", placeholder: "" },
        {
          name: "grossWeight",
          label: "Gesamtgewicht in kg",
          fieldType: "TextInput",
          placeholder: "",
          // Visible for Classes 2, 3, 4, 6, 10 (based on 'R' in matrix)
          visibleWhen: {
            field: "vehicleClass",
            operator: "regex",
            value: "(Klasse 2|Klasse 3|Klasse 4|Klasse 6|Klasse 10)",
          },
        },
        {
          name: "payload",
          label: "Nutzlast in kg",
          fieldType: "TextInput",
          placeholder: "",
          // Visible for Classes 2, 3, 4, 6, 10
          visibleWhen: {
            field: "vehicleClass",
            operator: "regex",
            value: "(Klasse 2|Klasse 3|Klasse 4|Klasse 6|Klasse 10)",
          },
        },
        {
          name: "hasSpecs",
          label: "Nähere Fahrzeugspezifikation",
          fieldType: "RadioGroup",
          content: ["Nein", "Ja"],
        },
        {
          name: "specs",
          label: "Welche?",
          fieldType: "TextInput",
          visibleWhen: { field: "hasSpecs", operator: "eq", value: "Ja" },
        },
      ],
    },
    {
      name: "3. Auf- und Einbauten",
      visibleWhen: { field: "vehicleClass", operator: "regex", value: "^Klasse" },
      fields: [
        {
          name: "bodyType",
          label: "Aufbaubezeichnung",
          fieldType: "Dropdown",
          placeholder: "Bitte auswählen",
          content: ["Kipper", "offener Kasten", "offener Kasten mit Plane und Spriegel", "geschlossener Kasten", "Wechselaufbau", "sonstiger Aufbau"],
          // Visible for Classes 3, 4, 6
          visibleWhen: {
            field: "vehicleClass",
            operator: "regex",
            value: "(Klasse 2|Klasse 3|Klasse 6)",
          },
        },
        {
          name: "hasInstallations",
          label: "Einbauten?",
          fieldType: "RadioGroup",
          content: ["Nein", "Ja"],
          // Visible for Classes 2, 3, 6, 7, 10
          visibleWhen: {
            field: "vehicleClass",
            operator: "regex",
            value: "(Klasse 2|Klasse 3|Klasse 5|Klasse 6|Klasse 10)",
          }
        },
        {
          name: "installationsDetails",
          label: "Welche?",
          fieldType: "TextInput",
          placeholder: "",
          visibleWhen: { field: "hasInstallations", operator: "eq", value: "Ja" },
        },
      ],
    },
    {
      name: "4. Wert",
      visibleWhen: { field: "vehicleClass", operator: "regex", value: "^Klasse" },
      fields: [
        { name: "vehicleValue", label: "Wert des Fahrzeugs (Neu/Zeitwert)", fieldType: "CurrencyInput", placeholder: "0,00" },
        {
          name: "labelingValue",
          label: "Wert der Beschriftung",
          fieldType: "CurrencyInput",
          placeholder: "0,00",
        },
        {
          name: "installationsValue",
          label: "Wert der Einbauten",
          fieldType: "CurrencyInput",
          placeholder: "0,00",
          visibleWhen: {
            all: [
              { not: { field: "vehicleClass", operator: "eq", value: "Klasse 1 PKW" } },
              { field: "hasInstallations", operator: "eq", value: true }
            ]
          }
        },
        {
          name: "bodyValue",
          label: "Wert der Aufbauten",
          fieldType: "CurrencyInput",
          placeholder: "0,00",
          visibleWhen: { field: "bodyType", operator: "eq", value: "sonstiger Aufbau" }
        },
        {
          name: "marketValue",
          label: "Marktwert gemäß Gutachten",
          fieldType: "CurrencyInput",
          placeholder: "0,00",
          visibleWhen: { field: "classicType", operator: "regex", value: "Youngtimer|Oldtimer" }
        },
        {
          name: "marketValueDate",
          label: "Gutachten vom (Datum)",
          fieldType: "DateInput",
          visibleWhen: { field: "classicType", operator: "regex", value: "Youngtimer|Oldtimer" }
        },
        {
          name: "restorationValue",
          label: "Wiederaufbauwert gemäß Gutachten",
          fieldType: "CurrencyInput",
          placeholder: "0,00",
          info: "falls angegeben, Hinweis auf Zusatzdeckung Wiederaufbauwert",
          visibleWhen: { field: "classicType", operator: "regex", value: "Youngtimer|Oldtimer" }
        },
        {
          name: "restorationValueDate",
          label: "Gutachten vom (Datum)",
          fieldType: "DateInput",
          visibleWhen: { field: "classicType", operator: "regex", value: "Youngtimer|Oldtimer" }
        },
        {
          name: "acquisitionType",
          label: "Worum handelt es sich?",
          fieldType: "RadioGroup",
          content: ["Kauf", "Mietkauf", "Leasing"]
        },
        // Kauf Fields
        {
          name: "purchaseCondition",
          label: "Zustand",
          fieldType: "RadioGroup",
          content: ["Neu", "Gebraucht"],
          visibleWhen: { field: "acquisitionType", operator: "eq", value: "Kauf" }
        },
        {
          name: "purchasePrice",
          label: "Listenpreis/Preis",
          fieldType: "CurrencyInput",
          placeholder: "0,00",
          visibleWhen: { field: "acquisitionType", operator: "eq", value: "Kauf" }
        },
        // Mietkauf Fields
        {
          name: "hirePurchasePayment",
          label: "Mietzahlung",
          fieldType: "CurrencyInput",
          placeholder: "0,00",
          visibleWhen: { field: "acquisitionType", operator: "eq", value: "Mietkauf" }
        },
        {
          name: "hirePurchaseDuration",
          label: "Wie lange (in Monaten)?",
          fieldType: "TextInput",
          placeholder: "z.B. 36",
          visibleWhen: { field: "acquisitionType", operator: "eq", value: "Mietkauf" }
        },
        {
          name: "hirePurchaseContractNum",
          label: "Vertragsnummer",
          fieldType: "TextInput",
          placeholder: "",
          visibleWhen: { field: "acquisitionType", operator: "eq", value: "Mietkauf" }
        },
        // Leasing Fields
        {
          name: "leasingRate",
          label: "Leasingrate",
          fieldType: "CurrencyInput",
          placeholder: "0,00",
          visibleWhen: { field: "acquisitionType", operator: "eq", value: "Leasing" }
        },
        {
          name: "leasingDuration",
          label: "Wie lange (in Monaten)?",
          fieldType: "TextInput",
          placeholder: "z.B. 48",
          visibleWhen: { field: "acquisitionType", operator: "eq", value: "Leasing" }
        },
        {
          name: "leasingContractNum",
          label: "Vertragsnummer",
          fieldType: "TextInput",
          placeholder: "",
          visibleWhen: { field: "acquisitionType", operator: "eq", value: "Leasing" }
        },
        {
          name: "acquisitionNotes",
          label: "Sonstiges",
          fieldType: "TextArea",
          placeholder: "",
          visibleWhen: { field: "acquisitionType", operator: "regex", value: "Mietkauf|Leasing" }
        },
        {
          name: "expense",
          label: "anerkannte Betriebsausgabe",
          fieldType: "RadioGroup",
          content: ["Nein", "Ja"],
        },
      ],
    },
    {
      name: "5. Zulassung und Verwendung",
      visibleWhen: { field: "vehicleClass", operator: "regex", value: "^Klasse" },
      fields: [
        { name: "firstRegistration", label: "Erstzulassung", fieldType: "DateInput", placeholder: "" },
        { name: "firstOwner", label: "Erstzulassung auf VN", fieldType: "DateInput", placeholder: "" },
        { name: "otherKeeper", label: "abweichender Halter", fieldType: "TextInput", placeholder: "" },
        // Different Usage Dropdowns based on Class
        {
          name: "usagePKW",
          label: "Verwendung",
          fieldType: "Dropdown",
          content: ["Eigenverwendung", "Auslieferungs-/Kurierdienst"],
          placeholder: "Bitte auswählen",
          visibleWhen: { field: "vehicleClass", operator: "regex", value: "Klasse 1" },
        },
        {
          name: "usageLieferwagen",
          label: "Verwendung",
          fieldType: "Dropdown",
          content: ["Privat- und Werkverkehr", "gewerblicher Güterverkehr"],
          placeholder: "Bitte auswählen",
          visibleWhen: { field: "vehicleClass", operator: "regex", value: "Klasse 2" },
        },
        {
          name: "usageLKW",
          label: "Verwendung",
          fieldType: "Dropdown",
          content: ["Werkverkehr", "gewerblicher Güterverkehr"],
          placeholder: "Bitte auswählen",
          visibleWhen: { field: "vehicleClass", operator: "regex", value: "Klasse 3" },
        },
        {
          name: "usageZug",
          label: "Verwendung",
          fieldType: "Dropdown",
          content: ["Werkverkehr", "gewerblicher Güterverkehr", "Land- & Forstwirtschaft"],
          placeholder: "Bitte auswählen",
          visibleWhen: { field: "vehicleClass", operator: "regex", value: "Klasse 4" },
        },
        {
          name: "usageTrailer",
          label: "Verwendung",
          fieldType: "Dropdown",
          content: ["Privat- und Werkverkehr", "gewerblicher Güterverkehr", "Land- & Forstwirtschaft", "Wohnwagenanhänger"],
          placeholder: "Bitte auswählen",
          visibleWhen: { field: "vehicleClass", operator: "regex", value: "Klasse 6" },
        },
        {
          name: "hasDrivingAreaOutsideEU",
          label: "Fahrgebiet außerhalb EU",
          fieldType: "RadioGroup",
          content: ["Nein", "Ja"],
        },
        {
          name: "drivingArea",
          label: "Welche?",
          fieldType: "TextInput",
          placeholder: "",
          visibleWhen: { field: "hasDrivingAreaOutsideEU", operator: "eq", value: "Ja" },
        },
        {
          name: "vehicleAbroad",
          label: "Befindet sich das Fahrzeug im Ausland?",
          fieldType: "RadioGroup",
          content: ["Nein", "Ja"],
        },
        {
          name: "vehicleAbroadLocation",
          label: "Wo befindet sich das Fahrzeug?",
          fieldType: "TextInput",
          placeholder: "z.B. Mallorca, Schweiz",
          visibleWhen: { field: "vehicleAbroad", operator: "eq", value: "Ja" },
        },
        {
          name: "raceTrack",
          label: "Touristenfahrten auf Rennstrecken",
          fieldType: "RadioGroup",
          content: ["Nein", "Ja"],
        },
        {
          name: "raceTrackDetails",
          label: "Was?",
          fieldType: "TextInput",
          visibleWhen: { field: "raceTrack", operator: "eq", value: "Ja" },
        },
        {
          name: "trackDays",
          label: "Nehmen Sie an Trackdays teil?",
          fieldType: "RadioGroup",
          content: ["Nein", "Ja"],
          visibleWhen: { field: "vehicleClass", operator: "eq", value: "Klasse 1 PKW" },
        },
        {
          name: "trackDaysDetails",
          label: "Welche?",
          fieldType: "TextInput",
          visibleWhen: {
            all: [
              { field: "vehicleClass", operator: "eq", value: "Klasse 1 PKW" },
              { field: "trackDays", operator: "eq", value: "Ja" }
            ]
          },
        },
        {
          name: "speedContest",
          label: "Nehmen Sie zusätzlich an Veranstaltungen zur Erzielung von Höchstgeschwindigkeiten teil?",
          fieldType: "RadioGroup",
          content: ["Nein", "Ja"],
          visibleWhen: { field: "vehicleClass", operator: "eq", value: "Klasse 1 PKW" },
        },
        {
          name: "speedContestDetails",
          label: "Welche?",
          fieldType: "TextInput",
          visibleWhen: {
            all: [
              { field: "vehicleClass", operator: "eq", value: "Klasse 1 PKW" },
              { field: "speedContest", operator: "eq", value: "Ja" }
            ]
          },
        },
      ],
    },
    {
      name: "6. Fahrerdaten",
      visibleWhen: { field: "vehicleClass", operator: "regex", value: "(Klasse 1|Klasse 2|Klasse 3|Klasse 4|Klasse 5|Klasse 9|Klasse 10)" },
      fields: [
        {
          name: "driverCircle",
          label: "Fahrerkreis",
          fieldType: "Dropdown",
          placeholder: "Bitte auswählen",
          content: ["VN", "VN + Partner", "VN + Kind", "Kinder", "Sonstige"],
          visibleWhen: { field: "vehicleClass", operator: "regex", value: "(Klasse 1|Klasse 2|Klasse 9|Klasse 10)" },
        },
        // VN Selection (Selection 1)
        {
          name: "mainDriverId",
          label: "Versicherungsnehmer (VN) auswählen",
          fieldType: "ContactSelection",
          visibleWhen: {
            all: [
              { field: "vehicleClass", operator: "regex", value: "(Klasse 1|Klasse 2|Klasse 9|Klasse 10)" },
              { field: "driverCircle", operator: "regex", value: "VN|Kinder" }
            ]
          }
        },
        {
          name: "mainDriverAge",
          label: "Fahreralter (VN)",
          fieldType: "Dropdown",
          placeholder: "Bitte auswählen",
          content: ["unter 23", "unter 65", "unter 70", "unter 75", "unter 80", "über 80"],
          visibleWhen: {
            all: [
              { field: "vehicleClass", operator: "regex", value: "(Klasse 1|Klasse 2|Klasse 9|Klasse 10)" },
              { field: "driverCircle", operator: "regex", value: "VN" }
            ]
          }
        },
        // Selection 2 (Partner/Kind) - logic handled in components
        {
          name: "secondaryDriverId",
          label: "Partner/Kind auswählen",
          fieldType: "RelationSelection",
          visibleWhen: {
            all: [
              { field: "vehicleClass", operator: "regex", value: "(Klasse 1|Klasse 2|Klasse 9|Klasse 10)" },
              { field: "driverCircle", operator: "regex", value: "VN \\+ Partner|VN \\+ Kind|Kinder" }
            ]
          }
        },
        {
          name: "secondaryDriverAge",
          label: "Fahreralter (Partner/Kind)",
          fieldType: "Dropdown",
          placeholder: "Bitte auswählen",
          content: ["unter 23", "unter 65", "unter 70", "unter 75", "unter 80", "über 80"],
          visibleWhen: {
            all: [
              { field: "vehicleClass", operator: "regex", value: "(Klasse 1|Klasse 2|Klasse 9|Klasse 10)" },
              { field: "driverCircle", operator: "regex", value: "VN \\+ Partner|VN \\+ Kind" }
            ]
          }
        },
        {
          name: "mileage",
          label: "Jährliche Fahrleistung",
          fieldType: "Dropdown",
          placeholder: "Bitte auswählen",
          content: ["bis 6.000", "bis 9.000", "bis 12.000", "bis 15.000", "bis 18.000", "bis 24.000", "bis 30.000", "über 30.000"],
          visibleWhen: { field: "vehicleClass", operator: "regex", value: "(Klasse 1|Klasse 2|Klasse 9|Klasse 10)" },
        },
        { name: "licenseDate", label: "Führerscheindatum", fieldType: "DateInput", placeholder: "" },
        {
          name: "accompaniedDriving",
          label: "Begleitetes Fahren",
          fieldType: "RadioGroup",
          content: ["Nein", "Ja"],
        },
        {
          name: "accompaniedDrivingDate",
          label: "Bis wann?",
          fieldType: "DateInput",
          visibleWhen: { field: "accompaniedDriving", operator: "eq", value: "Ja" },
        },
      ],
    },
    {
      name: "7. WKZ",
      visibleWhen: { field: "vehicleClass", operator: "regex", value: "^Klasse" },
      fields: [
        {
          name: "wkz",
          label: "WKZ",
          fieldType: "Combobox",
          placeholder: "WKZ suchen...",
          content: [
            "014 : Leichtkraftroller",
            "024 : Leichtkraftkrafträder",
            "003 : Krafträder und Kraftroller",
            "030 : Trikes",
            "031 : Quads",
            "072 : Selbstfahrervermietkrafträder",
            "112 : Pkw",
            "140 : Mietwagen",
            "150 : Taxis",
            "162 : Selbstfahrervermiet-Pkw",
            "127 : Wohnmobile (Campingfahrzeuge)",
            "172 : Selbstfahrervermiet-Wohnmobile",
            "541 : Wohnwagenanhänger",
            "251 : Lieferwagen Privat- und Werkverkehr",
            "261 : Lieferwagen Gewerblicher Güterverkehr",
            "272 : Selbstfahrervermiet-Lieferwagen",
            "351 : Lastkraftwagen Werkverkehr",
            "361 : Lastkraftwagen Gewerblicher Güterverkehr",
            "359 : Lehr-Lkw",
            "382 : Lkw im Umzugsverkehr",
            "372 : Selbstfahrervermiet-Lastkraftwagen",
            "401 : Zugmaschine Werkverkehr",
            "411 : Zugmaschine Gewerblicher Güterverkehr",
            "451 : Landwirtschaftlicher Zugmaschine and Raupenschlepper",
            "581 : Anhänger Privat- and Werkverkehr",
            "591 : Anhänger Gewerblicher Güterverkehr",
            "532 : Anhänger Umzugsverkehr",
            "551 : Anhänger zu landwirtschaftlichen Zugmaschinen",
            "542 : Anhänger in Sonderausführung",
            "561 : Anhänger von Fahrzeugen des Deutschen Roten Kreuzes",
            "571 : Anhänger Wechselaufbauten",
            "572 : Selbstfahrervermiet-Anhänger",
            "651 : Kraftomnibusse Linienverkehr",
            "661 : Kraftomnibusse Gelegenheitsverkehr",
            "621 : sonstige Kraftomnibusse",
            "701 : Abschleppwagen",
            "702 : Arbeitsmaschinen",
            "712 : Auto-/Mobilkräne",
            "713 : Betonpumpen",
            "542 : Anhänger Arbeitsmaschinen",
            "703 : Fahrbare Küchen, Feuerwehrmannschafts- and Gerätewagen",
            "705 : Straßenreinigungs- and Sprengwagen, Müll- and Fäkalienabfuhrwagen",
            "707 : Krankenwagen",
            "708 : Hub- and Gabelstapler über 20 km/h auf beschränkt öffentlichen and öffentlichen Verkehrsflächen",
            "709 : Leichenwagen",
            "719 : Wagnisse, die nicht an anderer Stelle im Tarif genannt sind",
            "195 : Oldtimer-Pkw ohne Vermietung",
            "095 : Oldtimer-Kraftrad ohne Vermietung",
            "196 : Oldtimer-Campingfahrzeug ohne Vermietung",
            "295 : Oldtimer-Lieferwagen ohne Vermietung",
            "395 : Oldtimer-Lastkraftwagen ohne Vermietung",
            "495 : Oldtimer-Zugmaschine ohne Vermietung",
            "695 : Oldtimer-Omnibus ohne Vermietung",
            "795 : Oldtimer-Fahrzeug ohne Vermietung",
            "496 : Oldtimer-Landwirtschaftliche Zugmaschine ohne Vermietung",
            "595 : Oldtimer-Anhänger ohne Vermietung",
          ],
        },
      ],
    },
  ],
};

export {
  shareholderForm,
  contactPersonForm,
  childrenForm,
  companyForm,
  privat_contact_form,
  partner,
  buildingExtensionForm,
  kennzahlenForm,
  kfzForm,
};
